import { memo } from 'react';
import useVideoThumbnail from './useVideoThumbnail';
import Icon from '@/components/atoms/Icon';

/**
 * The uploading-video card shown at the top of both modal screens (Figma keeps the upload
 * visible while the user sets preferences). Shows a first-frame thumbnail (or a placeholder
 * for audio / undecodable files), the file name, a progress bar, live %, and helper text.
 */
const UploadProgressCard = ({ file, fileName, percentage }: { file?: File; fileName?: string; percentage: number }) => {
  const thumbnail = useVideoThumbnail(file);

  return (
    <div className="flex items-center gap-4 rounded-lg border border-slate-200 p-3">
      {thumbnail ? (
        <img
          src={thumbnail}
          alt=""
          className="h-14 w-24 shrink-0 rounded-md object-cover"
          data-testid="upload-thumbnail"
        />
      ) : (
        <div
          className="flex h-14 w-24 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-400"
          data-testid="upload-thumbnail-placeholder"
        >
          <Icon name="IconFileUpload" size={20} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2 text-sm">
          <span className="truncate font-medium text-slate-900">{fileName}</span>
          <span className="shrink-0 text-slate-500">{percentage}%</span>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full bg-theme-active transition-all duration-150"
            style={{ width: `${percentage}%` }}
            data-testid="upload-progress-bar"
          />
        </div>
        {percentage < 100 && <p className="mt-2 text-xs text-slate-500">Uploading video...this might take a bit.</p>}
      </div>
    </div>
  );
};

export default memo(UploadProgressCard);
